import { Box, Chip, IconButton, Typography } from "@mui/material"
import { ChatOutlined, MailOutlined, PhoneIphoneOutlined } from "@mui-symbols-material/w300"
import type { CellContext, ColumnDef } from "@tanstack/react-table"
import React from "react"

import { CompanyContactActionsMenu } from "./CompanyContactActionsMenu"
import { getPrimaryContactMethods } from "./utils"

import type { Contact } from "@/graphql/codegen/graphql"

export const useCompanyContactsTable = ({
  onCommentClick,
  onViewClick,
}: {
  onCommentClick: (contact: Contact) => void
  onViewClick: (contact: Contact) => void
}): { columns: ColumnDef<Contact>[] } => {
  const columns: ColumnDef<Contact>[] = [
    {
      id: "contact_info",
      accessorKey: "",
      header: "Contact Information",
      cell: ({ row }: CellContext<Contact, unknown>): React.ReactNode => {
        const { firstName, lastName, contactMethod = null } = row.original

        const { email, phone, phoneExt } = getPrimaryContactMethods(contactMethod)
        return (
          <>
            {firstName} {lastName}
            <Box className='my-2 flex items-center gap-2'>
              {email && (
                <>
                  <MailOutlined fontSize='small' className='text-gray-600' />
                  <Typography
                    variant='body2'
                    className='max-w-[calc(100%-24px)] truncate text-gray-700'
                    fontWeight='normal'
                  >
                    {email}
                  </Typography>
                </>
              )}
              {phone && (
                <>
                  <PhoneIphoneOutlined fontSize='small' className='text-gray-600' />
                  <Typography
                    variant='body2'
                    className='max-w-[calc(100%-24px)] truncate text-gray-700'
                    fontWeight='normal'
                  >
                    {phone} Ext: {phoneExt}
                  </Typography>
                </>
              )}
            </Box>
          </>
        )
      },
      enableSorting: false,
    },
    {
      id: "subscriptions_locations",
      accessorKey: "",
      header: "Subscriptions and Locations",
      cell: ({ row }: CellContext<Contact, unknown>): React.ReactNode => {
        const { contactSubscriptions, contactLocations2 } = row.original
        return (
          <>
            <div>
              {contactSubscriptions.map((subscription) =>
                subscription ? (
                  <Chip
                    key={subscription.contactSubscriptionId}
                    label={subscription.name}
                    className='mb-2 mr-1 rounded-sm bg-tertiary-yellow-300 font-[12px] text-gray-900'
                  />
                ) : null
              )}
            </div>
            <div>
              {contactLocations2?.map((contactLocation) => (
                <Chip
                  key={contactLocation!.location.locationId}
                  label={contactLocation!.location.name}
                  className='mb-2 mr-1 rounded-sm font-[12px] text-gray-900'
                />
              ))}
            </div>
          </>
        )
      },
      enableSorting: false,
    },
    {
      accessorKey: "actions",
      header: "",
      cell: ({ row }: CellContext<Contact, unknown>) => {
        return (
          <div className='flex items-center justify-end gap-4'>
            <IconButton
              type='button'
              className='size-6 rounded bg-gray-100 text-primary hover:bg-primary-100'
              aria-label='comments'
              onClick={(event) => {
                event.stopPropagation()
                onCommentClick?.(row.original)
              }}
            >
              <ChatOutlined />
            </IconButton>
            <CompanyContactActionsMenu row={row} onViewClick={onViewClick} />
          </div>
        )
      },
      size: 50,
      enableSorting: false,
    },
  ]

  return { columns }
}
